/** @jsx jsx */
import React, { useCallback, useRef, useEffect } from "react"
import { jsx } from "theme-ui"

interface DesktopIconProps {
  id: string
  icon: string
  label: string
  isSelected: boolean
  onSelect: (id: string) => void
  onOpen: (id: string) => void
}

// On touch devices, single tap opens the window (no double-click needed)
const isTouchDevice =
  typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0)

const DesktopIcon = ({ id, icon, label, isSelected, onSelect, onOpen }: DesktopIconProps) => {
  const clickCountRef = useRef(0)
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Ensure pending single-click timer is cleared on unmount
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
    }
  }, [])

  const handleClick = useCallback(() => {
    // Touch devices: single tap opens immediately
    if (isTouchDevice) {
      onOpen(id)
      return
    }

    // Desktop: double-click to open, single-click to select
    clickCountRef.current += 1

    if (clickCountRef.current === 1) {
      onSelect(id)
      clickTimerRef.current = setTimeout(() => {
        clickCountRef.current = 0
      }, 300)
    } else if (clickCountRef.current === 2) {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current)
        clickTimerRef.current = null
      }
      clickCountRef.current = 0
      onOpen(id)
    }
  }, [id, onSelect, onOpen])

  return (
    <div
      onClick={handleClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "6px",
        width: "100px",
        p: "6px",
        cursor: "pointer",
        border: isSelected ? "1px dashed rgba(var(--retro-primary-rgb), 0.6)" : "1px solid transparent",
        background: isSelected ? "rgba(var(--retro-primary-rgb), 0.08)" : "transparent",
        borderRadius: "2px",
        transition: "all 0.15s ease",
        "&:hover": {
          background: "rgba(var(--retro-primary-rgb), 0.06)",
          border: "1px dashed rgba(var(--retro-primary-rgb), 0.4)",
        },
        userSelect: "none",
      }}
    >
      <span
        sx={{
          fontSize: "32px",
          lineHeight: 1,
          display: "block",
          textAlign: "center",
          filter: isSelected ? "drop-shadow(0 0 8px rgba(var(--retro-primary-rgb), 0.7))" : "none",
        }}
      >
        {icon}
      </span>
      <span
        sx={{
          fontFamily: '"Press Start 2P", monospace',
          fontSize: "8px",
          color: "var(--retro-primary)",
          textAlign: "center",
          textShadow: "0 0 6px rgba(var(--retro-primary-rgb), 0.8)",
          lineHeight: 1.4,
          wordBreak: "break-word",
          width: "100%",
        }}
      >
        {label}
      </span>
    </div>
  )
}

export default DesktopIcon
